import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaVideo, FaFileAlt } from "react-icons/fa";
import axios from "axios";

/* ================= BRAND ================= */

const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

/* ================= WRAPPER ================= */

const Wrapper = styled.div`
  font-family: "Poppins", sans-serif;
  min-height: 100vh;
  padding: 80px 20px;
  
  @media (max-width: 768px) {
    padding: 60px 16px;
  }

  @media (max-width: 480px) {
    padding: 40px 12px;
  }
`;

/* ================= TITLES ================= */

const Title = styled.h1`
  text-align: center;
  font-size: 2.3rem;
  margin-bottom: 10px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 480px) {
    font-size: 1.7rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 40px;
  color: #64748b;
  font-size: 1rem;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

/* ================= CARD ================= */

const Card = styled(motion.div)`
  background: #0f172a;
  border-radius: 20px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  gap: 3rem;
  padding: 40px;
  border: 1px solid #1e293b;
  box-shadow: 0 20px 40px rgba(0, 0, 40, 0.2);

  @media (max-width: 992px) {
    flex-direction: column;
    gap: 2rem;
    padding: 30px;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

/* ================= LEFT ================= */

const Left = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 20px;
  color: #fff;
  font-weight: 600;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #cbd5e1;
  display: block;
  margin-bottom: 6px;
`;

const LeftInput = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #1e293b;
  background: #020617;
  color: #fff;
  margin-bottom: 15px;

  &:focus {
    border-color: #0047ff;
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #1e293b;
  background: #020617;
  color: #fff;
  margin-bottom: 20px;

  &:focus {
    border-color: #0047ff;
    outline: none;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-weight: 600;
  transition: 0.3s;

  &:hover {
    transform: translateY(-2px);
  }
`;

/* ================= RIGHT ================= */

const Right = styled.div`
  flex: 1;
  background: #020617;
  padding: 25px;
  border-radius: 16px;
  border: 1px solid #1e293b;

  @media (max-width: 992px) {
    padding: 20px;
  }
`;

const UploadBox = styled.div`
  width: 100%;
  border: 2px dashed #334155;
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  margin-bottom: 20px;
  transition: 0.3s;

  &:hover {
    border-color: #0047ff;
  }
`;

const UploadTitle = styled.p`
  margin: 10px 0;
  color: #cbd5e1;
  font-size: 0.9rem;
`;

const RightInput = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #1e293b;
  background: #0f172a;
  color: #fff;
`;

/* ================= COMPONENT ================= */

const Sell = () => {
  const [projectName, setProjectName] = useState("");
  const [technology, setTechnology] = useState("");
  const [description, setDescription] = useState("");
  const [video, setVideo] = useState(null);
  const [projectFiles, setProjectFiles] = useState([]);

  const handleVideoChange = (e) => setVideo(e.target.files[0]);
  const handleFileChange = (e) => setProjectFiles(Array.from(e.target.files));

  const handleSellProject = async () => {
    const formData = new FormData();
    formData.append("projectName", projectName);
    formData.append("technology", technology);
    formData.append("description", description);

    if (video) formData.append("video", video);
    projectFiles.forEach((file) => formData.append("files", file));

    try {
      await axios.post("http://165.22.218.70:1112/api/projects", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("✅ Your project was successfully saved!");
    } catch (error) {
      console.error(error);
      alert("❌ Error saving project. Try again!");
    }
  };

  return (
    <Wrapper>
      <Title>Sell Your Project</Title>

      <Subtitle>
        Provide your project info and upload demo files to make your listing stand out.
      </Subtitle>

      <Card initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* LEFT */}
        <Left>
          <SectionTitle>Project Details</SectionTitle>

          <Label>Project Name</Label>
          <LeftInput
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />

          <Label>Technology Used</Label>
          <LeftInput
            value={technology}
            onChange={(e) => setTechnology(e.target.value)}
          />

          <Label>Description</Label>
          <Textarea
            rows="4"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Button onClick={handleSellProject}>
            Sell Project
          </Button>
        </Left>

        {/* RIGHT */}
        <Right>
          <SectionTitle>Upload Demo Files</SectionTitle>

          <UploadBox>
            <FaVideo size={32} color="#00c4ff" />
            <UploadTitle>Upload Demo Video</UploadTitle>
            <RightInput
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
            />
          </UploadBox>

          <UploadBox>
            <FaFileAlt size={30} color="#00c4ff" />
            <UploadTitle>Upload Project Files</UploadTitle>
            <RightInput type="file" multiple onChange={handleFileChange} />
          </UploadBox>
        </Right>
      </Card>
    </Wrapper>
  );
};

export default Sell;